import React, { useMemo } from 'react';

import { Badge, Modal, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';

import useCan from '@shared/permissions/useCan';

import type { PreviewEntry, PreviewItem, PreviewSplitSummary } from '@services/operator/replenishmentService';

const { Text } = Typography;

// 補貨計畫預覽 — 把每個 need (PreviewEntry) 的來源 fan-out (items) 攤平成單列。
// 一個 entry 可能對應多個來源批號／預備儲位（FEFO 分配），故用 entry+item 組合做 row key。
interface FlatRow {
  rowKey: string;
  // entry 層欄位（同一 entry 的多個來源列共用）— 第一列才顯示，其餘列省略避免重複。
  isFirstOfEntry: boolean;
  product_name: string | null;
  product_sku: string | null;
  need: number;
  shortage: number;
  over_capacity: boolean;
  // item 層欄位
  batch: string | null;
  from_shelf_location: string | null;
  to_shelf_location: string | null;
  suggested_quantity: number;
}

export interface PreviewModalProps {
  open: boolean;
  entries: PreviewEntry[];
  // 切分摘要。描述本次預覽會產生哪些補貨單（依 zone fan-out）；N>1 時列出各單明細數/數量。
  split: PreviewSplitSummary[];
  confirmLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function flattenEntries(entries: PreviewEntry[]): FlatRow[] {
  const rows: FlatRow[] = [];
  entries.forEach((entry, entryIdx) => {
    const items: PreviewItem[] = entry.items;
    if (items.length === 0) {
      // 沒有任何來源可分配（完全缺貨）— 仍要顯示該 need 的 shortage。
      rows.push({
        rowKey: `${entryIdx}-empty`,
        isFirstOfEntry: true,
        product_name: entry.product_name,
        product_sku: entry.product_sku,
        need: entry.need,
        shortage: entry.shortage,
        over_capacity: entry.over_capacity,
        batch: null,
        from_shelf_location: null,
        to_shelf_location: entry.shelf_location,
        suggested_quantity: 0,
      });
      return;
    }
    items.forEach((item, itemIdx) => {
      rows.push({
        rowKey: `${entryIdx}-${itemIdx}-${item.inventory_id}`,
        isFirstOfEntry: itemIdx === 0,
        product_name: entry.product_name,
        product_sku: entry.product_sku,
        need: entry.need,
        shortage: entry.shortage,
        over_capacity: entry.over_capacity,
        batch: item.batch,
        from_shelf_location: item.from_shelf_location,
        to_shelf_location: item.to_shelf_location,
        suggested_quantity: item.suggested_quantity,
      });
    });
  });
  return rows;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  open,
  entries,
  split,
  confirmLoading = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const canWrite = useCan()('inventories:write');

  const rows = useMemo(() => flattenEntries(entries), [entries]);

  const columns = useMemo<ColumnsType<FlatRow>>(
    () => [
      {
        title: t('operator.replenishment.preview.columns.product'),
        key: 'product',
        render: (_: unknown, row: FlatRow) =>
          row.isFirstOfEntry ? (
            <div>
              <div>{row.product_name ?? '-'}</div>
              {row.product_sku && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {row.product_sku}
                </Text>
              )}
            </div>
          ) : null,
      },
      {
        title: t('operator.replenishment.preview.columns.batch'),
        dataIndex: 'batch',
        key: 'batch',
        render: (batch: string | null) => batch ?? '-',
      },
      {
        title: t('operator.replenishment.preview.columns.from_shelf'),
        dataIndex: 'from_shelf_location',
        key: 'from_shelf_location',
        render: (loc: string | null) => loc ?? '-',
      },
      {
        title: t('operator.replenishment.preview.columns.to_shelf'),
        dataIndex: 'to_shelf_location',
        key: 'to_shelf_location',
        render: (loc: string | null) => loc ?? '-',
      },
      {
        title: t('operator.replenishment.preview.columns.suggested_quantity'),
        dataIndex: 'suggested_quantity',
        key: 'suggested_quantity',
      },
      {
        title: t('operator.replenishment.preview.columns.need'),
        key: 'need',
        render: (_: unknown, row: FlatRow) =>
          row.isFirstOfEntry ? (
            <Space size={4}>
              <span>{row.need}</span>
              {row.shortage > 0 && (
                <Tag color="red">
                  {t('operator.replenishment.preview.shortage', {
                    count: row.shortage,
                    defaultValue: 'shortage {{count}}',
                  })}
                </Tag>
              )}
              {row.over_capacity && (
                <Badge
                  status="warning"
                  text={t('operator.replenishment.preview.over_capacity')}
                />
              )}
            </Space>
          ) : null,
      },
    ],
    [t],
  );

  return (
    <Modal
      open={open}
      title={t('operator.replenishment.preview.title')}
      okText={t('operator.replenishment.preview.generate')}
      cancelText={t('common.button.cancel')}
      onOk={() => onConfirm()}
      onCancel={onCancel}
      okButtonProps={{ disabled: !canWrite, loading: confirmLoading }}
      width={900}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {split.length > 1 && (
          <div>
            <Text strong>
              {t('operator.replenishment.needs.split.summary_count', { count: split.length })}
            </Text>
            <div style={{ marginTop: 8 }}>
              {split.map((s) => (
                <Text key={s.order_index} type="secondary" style={{ display: 'block' }}>
                  {`${s.zone_name ?? t('operator.replenishment.needs.split.unzoned')} · `}
                  {t('operator.replenishment.needs.split.zone_line', {
                    items: s.item_count,
                    qty: s.total_quantity,
                  })}
                </Text>
              ))}
            </div>
          </div>
        )}
        <Table<FlatRow>
          rowKey="rowKey"
          size="small"
          pagination={false}
          columns={columns}
          dataSource={rows}
        />
      </Space>
    </Modal>
  );
};

export default PreviewModal;
